/**
 * @fileoverview Standalone Angular 21 Component for Chat V2 Unified Action Card.
 * Replicates the mobile Flutter `message_actions.dart` reference with sequence / ASYNC badges,
 * active/inactive strikethrough toggling, expandable details drawer with JSON inspection,
 * contextual button pairs, and in-place optimistic state transitions.
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import {
  AiAction,
  separateRapiderMetadata,
  isServerAction as isBaseServerAction,
} from '../../../ai-chat.config';

/**
 * Configuration constants and default localized strings for the Chat V2 Action Card.
 * Extracted into a pure object to prevent hardcoded strings in logic per architecture rules.
 */
export const CHAT_V2_ACTION_CARD_CONFIG = {
  texts: {
    asyncBadgeText: 'ASYNC',
    actionBadgeText: 'Action',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    processed: 'Processed',
    confirmed: 'Confirmed',
    skipped: 'Skipped',
    allowOnce: 'Allow Once',
    alwaysAllow: 'Always Allow',
    cancel: 'Cancel',
    save: 'Save',
    discard: 'Discard',
    delete: 'Delete',
    confirm: 'Confirm',
    runAction: 'Run Action',
    reject: 'Reject',
    serverAction: 'Server Action',
    clientAction: 'Client Action',
    httpRequest: 'HTTP Request',
    defaultActionTitle: 'Action',
  },
  colors: {
    darkBadgeBg: '#1E1E1E',
    asyncBadgeBg: '#F7F7F8',
    asyncBadgeBorder: '#E5E5E5',
    inactiveBadgeBgLight: '#E0E0E0',
    inactiveBadgeBgDark: '#3D4047',
    successGreen: '#2E7D32',
    deleteRed: '#C62828',
    secondaryText: '#757575',
  },
  httpMethods: {
    get: 'GET',
    post: 'POST',
    put: 'PUT',
    patch: 'PATCH',
    delete: 'DELETE',
  },
} as const;

/**
 * Interface representing resolved button labels and visual styling for action execution.
 */
export interface ChatV2ActionButtonLabels {
  /** Label for the primary confirmation button */
  primary: string;
  /** Label for secondary button option (e.g. Always Allow for server actions) */
  primaryOption2?: string;
  /** Label for the secondary cancellation/discard button */
  secondary: string;
  /** Primary button visual style variant */
  primaryStyle: 'dark' | 'outline' | 'red';
  /** Whether the action represents a dangerous delete operation */
  isDelete: boolean;
  /** Whether the action is an MCP / server action that supports 'Always Allow' */
  isServerAction: boolean;
}

/**
 * Interface for action execution events emitted to the parent container.
 */
export interface ChatV2ActionExecuteEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any;
  confirmed: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
}

/**
 * Interface for action active state toggle events emitted to the parent container.
 */
export interface ChatV2ActionToggleEvent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: any;
  isActive: boolean;
}

/**
 * Resolved HTTP metadata extracted from an endpoint action.
 */
export interface ChatV2HttpInfo {
  method: string;
  path: string;
}

/**
 * PURE FUNCTION: Formats a raw identifier or operation string into capitalized title words.
 *
 * @param raw - Raw operation identifier (e.g. 'workspace.createRecord', 'run_ai_function').
 * @returns Formatted human-readable string.
 */
export function formatOperationTitle(raw: string): string {
  if (!raw || typeof raw !== 'string') {
    return '';
  }
  const clean = raw.includes('.') ? raw.split('.').pop() || raw : raw;
  const words = clean
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim();
  return words
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * PURE FUNCTION: Determines whether the action represents an asynchronous background workflow capability.
 * Evaluates sourceType, action type, and operation identifiers matching Flutter reference logic.
 *
 * @param action - Action instance to evaluate.
 * @returns True if the action is an async workflow capability.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAsyncAction(action: AiAction | any): boolean {
  if (!action) {
    return false;
  }
  const sourceType = action.payload?.sourceType;
  if (
    sourceType === 'workflow' ||
    sourceType === 'workflow_event' ||
    sourceType === 'workflow_step' ||
    sourceType === 'ai_function' ||
    sourceType === 'async'
  ) {
    return true;
  }
  if (action.type === 'workflow' || action.type === 'rapider-workflow') {
    return true;
  }
  if (action.isWorkflowRunAction === true) {
    return true;
  }
  const op =
    typeof action.operation === 'string' ? action.operation.toLowerCase() : '';
  if (
    op === 'run_ai_function' ||
    op === 'run_workflow_event' ||
    op === 'run_workflow_step_function' ||
    op.includes('workflow')
  ) {
    return true;
  }
  const cap =
    typeof action.payload?.capabilityKey === 'string'
      ? action.payload.capabilityKey.toLowerCase()
      : '';
  if (cap.includes('workflow')) {
    return true;
  }
  return false;
}

/**
 * PURE FUNCTION: Determines whether the action represents a backend MCP / server action tool call.
 *
 * @param action - Action instance to evaluate.
 * @returns True if the action represents a server execution.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isActionServerExecution(action: AiAction | any): boolean {
  if (!action) {
    return false;
  }
  if (isBaseServerAction(action)) {
    return true;
  }
  if (action.type === 'server_action' || action.execution === 'server') {
    return true;
  }
  if (action.payload?.sourceType === 'server_action') {
    return true;
  }
  if (action.payload?.input && !action.payload?.http) {
    return true;
  }
  return false;
}

/**
 * PURE FUNCTION: Determines whether the action represents an endpoint HTTP action.
 *
 * @param action - Action instance to evaluate.
 * @returns True if the action is an HTTP endpoint action.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isEndpointAction(action: AiAction | any): boolean {
  if (!action) {
    return false;
  }
  return Boolean(
    action.type === 'endpoint' ||
      action.payload?.http != null ||
      action.httpInfo != null,
  );
}

/**
 * PURE FUNCTION: Extracts HTTP method and path metadata from endpoint actions.
 *
 * @param action - Action instance to inspect.
 * @returns HTTP info object containing method and path, or null if not an endpoint.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionHttpInfo(
  action: AiAction | any,
): ChatV2HttpInfo | null {
  if (!action) {
    return null;
  }
  if (action.payload?.http) {
    const http = action.payload.http;
    return {
      method: (
        http.method || CHAT_V2_ACTION_CARD_CONFIG.httpMethods.post
      ).toUpperCase(),
      path: http.path || '/',
    };
  }
  if (action.httpInfo) {
    return {
      method: (
        action.httpInfo.method || CHAT_V2_ACTION_CARD_CONFIG.httpMethods.get
      ).toUpperCase(),
      path: action.httpInfo.path || '/',
    };
  }
  return null;
}

/**
 * PURE FUNCTION: Returns the Tailwind CSS styling classes for HTTP method badges.
 *
 * @param method - HTTP method string (e.g. 'GET', 'POST', 'DELETE').
 * @returns CSS class string for background, text color, and border.
 */
export function getHttpMethodBadgeClass(method?: string): string {
  const m = (method || '').toUpperCase();
  switch (m) {
    case 'GET':
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800';
    case 'POST':
      return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800';
    case 'PUT':
    case 'PATCH':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800';
    case 'DELETE':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
  }
}

/**
 * PURE FUNCTION: Derives the primary human-readable title for an action card.
 * Priority: explicit title -> formatted capability/method -> formatted operation + entity -> description -> fallback.
 *
 * @param action - Action instance to evaluate.
 * @returns Human-readable primary action title.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionDisplayTitle(action: AiAction | any): string {
  if (!action) {
    return CHAT_V2_ACTION_CARD_CONFIG.texts.defaultActionTitle;
  }

  // 1. Explicit title input
  if (typeof action.title === 'string' && action.title.trim()) {
    return action.title.trim();
  }

  // 2. Server / MCP Action
  if (isActionServerExecution(action)) {
    if (
      typeof action.payload?.capabilityKey === 'string' &&
      action.payload.capabilityKey.trim()
    ) {
      return formatOperationTitle(action.payload.capabilityKey);
    }
    if (typeof action.description === 'string' && action.description.trim()) {
      const cleaned = action.description.replace(/^execute\s+/i, '').trim();
      if (cleaned) {
        return formatOperationTitle(cleaned);
      }
    }
    return CHAT_V2_ACTION_CARD_CONFIG.texts.serverAction;
  }

  // 3. Operation with entity name context
  const op =
    typeof action.operation === 'string' ? action.operation.trim() : '';
  const entity =
    action.entityName ||
    action.payload?.http?.projectModelName ||
    action.payload?.http?.returnType;
  if (op) {
    const formattedOp = formatOperationTitle(op);
    if (entity && typeof entity === 'string' && entity.trim()) {
      return `${formattedOp} ${entity.trim()}`;
    }
    return formattedOp;
  }

  // 4. Description fallback
  if (typeof action.description === 'string' && action.description.trim()) {
    return action.description.trim();
  }

  // 5. Endpoint path fallback
  if (isEndpointAction(action)) {
    const http = getActionHttpInfo(action);
    if (http?.path) {
      return `${http.method} ${http.path}`;
    }
    return CHAT_V2_ACTION_CARD_CONFIG.texts.httpRequest;
  }

  return CHAT_V2_ACTION_CARD_CONFIG.texts.defaultActionTitle;
}

/**
 * PURE FUNCTION: Derives the secondary human-readable description for an action card.
 * Priority: description (if distinct from title) -> endpoint path -> entity name -> capability -> fallback.
 *
 * @param action - Action instance to evaluate.
 * @returns Human-readable secondary description or subtitle.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionDisplayDescription(action: AiAction | any): string {
  if (!action) {
    return '';
  }

  const title = getActionDisplayTitle(action);

  if (typeof action.description === 'string' && action.description.trim()) {
    const trimmedDesc = action.description.trim();
    if (trimmedDesc.toLowerCase() !== title.toLowerCase()) {
      return trimmedDesc;
    }
  }

  const http = getActionHttpInfo(action);
  if (http?.path) {
    return `${http.method} ${http.path}`;
  }

  const entity =
    action.entityName ||
    action.payload?.http?.projectModelName ||
    action.payload?.http?.returnType;
  if (entity && typeof entity === 'string' && entity.trim()) {
    return entity.trim();
  }

  if (
    typeof action.payload?.capabilityKey === 'string' &&
    action.payload.capabilityKey.trim()
  ) {
    return action.payload.capabilityKey.trim();
  }

  return typeof action.description === 'string'
    ? action.description.trim()
    : '';
}

/**
 * PURE FUNCTION: Extracts and cleanses the JSON payload data to display inside the details drawer.
 * For HTTP endpoint actions, only extracts explicit request `body` or `params` (never falls back to
 * dumping internal `http` configuration such as `returnType`, `responseMessages`, or `proxyPath`).
 * Strips internal platform `__rapider` metadata fields cleanly.
 *
 * @param action - Action instance to inspect.
 * @returns Clean JSON payload object for inspection, or `{}` when the action has no request payload (e.g. DELETE).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActionDisplayPayload(action: AiAction | any): any {
  if (!action || !action.payload) {
    return {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let raw: any;
  if (isActionServerExecution(action)) {
    raw = action.payload.input || action.payload;
  } else if (isEndpointAction(action)) {
    const http = action.payload.http;
    const hasNonEmptyBody =
      http?.body && typeof http.body === 'object'
        ? Object.keys(http.body).length > 0
        : Boolean(http?.body);
    raw = hasNonEmptyBody ? http?.body : http?.params;
  } else if (action.payload.payload) {
    raw = action.payload.payload;
  } else {
    raw = action.payload.input || action.payload;
  }

  if (raw && typeof raw === 'object') {
    const { cleansed } = separateRapiderMetadata(raw);
    return cleansed !== null && cleansed !== undefined ? cleansed : raw;
  }
  return raw || {};
}

/**
 * PURE FUNCTION: Determines whether the action has a non-empty JSON payload to display in the details drawer.
 * For HTTP endpoint actions without a body or query params (such as DELETE), returns false so the drawer
 * displays only the HTTP method and path without rendering an empty or noisy JSON box.
 *
 * @param action - Action instance to inspect.
 * @returns True when a non-empty payload exists to render in the JSON viewer.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hasActionDisplayPayload(action: AiAction | any): boolean {
  const payload = getActionDisplayPayload(action);
  if (!payload || typeof payload !== 'object') {
    return Boolean(payload);
  }
  return Object.keys(payload).length > 0;
}

/**
 * PURE FUNCTION: Resolves the contextual action button labels and visual styling configuration.
 *
 * @param action - Action instance to evaluate.
 * @param uiConfig - Optional UI configuration overrides.
 * @returns Resolved button labels, styles, and action type flags.
 */
export function getOperationButtonLabels(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action: AiAction | any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uiConfig?: any,
): ChatV2ActionButtonLabels {
  if (isActionServerExecution(action)) {
    return {
      primary: CHAT_V2_ACTION_CARD_CONFIG.texts.allowOnce,
      primaryOption2: CHAT_V2_ACTION_CARD_CONFIG.texts.alwaysAllow,
      secondary: CHAT_V2_ACTION_CARD_CONFIG.texts.cancel,
      primaryStyle: 'outline',
      isDelete: false,
      isServerAction: true,
    };
  }

  const op = action?.operation
    ? String(action.operation).toLowerCase().trim()
    : '';

  if (op === 'delete' || op === 'destroy' || op === 'remove') {
    return {
      primary: CHAT_V2_ACTION_CARD_CONFIG.texts.delete,
      secondary: CHAT_V2_ACTION_CARD_CONFIG.texts.cancel,
      primaryStyle: 'red',
      isDelete: true,
      isServerAction: false,
    };
  }

  if (
    op === 'create' ||
    op === 'insert' ||
    op === 'add' ||
    op === 'update' ||
    op === 'patch' ||
    op === 'save'
  ) {
    return {
      primary: CHAT_V2_ACTION_CARD_CONFIG.texts.save,
      secondary: CHAT_V2_ACTION_CARD_CONFIG.texts.discard,
      primaryStyle: 'dark',
      isDelete: false,
      isServerAction: false,
    };
  }

  if (
    uiConfig?.operationButtons &&
    action?.operation &&
    uiConfig.operationButtons[action.operation]
  ) {
    const cfg = uiConfig.operationButtons[action.operation];
    return {
      primary: cfg.primaryText || CHAT_V2_ACTION_CARD_CONFIG.texts.confirm,
      secondary: cfg.secondaryText || CHAT_V2_ACTION_CARD_CONFIG.texts.cancel,
      primaryStyle: 'dark',
      isDelete: false,
      isServerAction: false,
    };
  }

  return {
    primary: CHAT_V2_ACTION_CARD_CONFIG.texts.confirm,
    secondary: CHAT_V2_ACTION_CARD_CONFIG.texts.cancel,
    primaryStyle: 'dark',
    isDelete: false,
    isServerAction: false,
  };
}

/**
 * Standalone Angular 21 component rendering a unified action card at the end of chat messages.
 */
@Component({
  selector: 'rapider-chat-v2-action-card',
  standalone: true,
  imports: [CommonModule, FormsModule, RapiderJsonViewerComponent],
  templateUrl: './chat-v2-action-card.component.html',
  styleUrls: ['./chat-v2-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatV2ActionCardComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Authoritative config object exposed for template consumption.
   */
  public readonly config = CHAT_V2_ACTION_CARD_CONFIG;

  /**
   * The AI action object to render.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() action: AiAction | any;

  /**
   * 0-based sequence index within the message actions list.
   */
  @Input() index = 0;

  /**
   * Total count of actions rendered in the current group.
   */
  @Input() totalCount = 1;

  /**
   * Whether the action has already been executed or marked completed by the backend.
   */
  @Input() isCompleted = false;

  /**
   * Whether this card belongs to the latest message in the conversation thread.
   */
  @Input() isLatest = true;

  /**
   * Optimistic completion flag set immediately upon user button click.
   */
  @Input() isOptimisticDone = false;

  /**
   * Active toggle state (Cupertino switch). When inactive, the action is skipped and styled with strikethrough.
   */
  @Input() isActive = true;

  /**
   * Whether the card shows its own Save / Cancel / Allow buttons. False when the card is one of
   * several actions of a message: the message bubble then shows ONE "Run N actions" / "Reject"
   * bar for the whole batch (mobile parity) and the card keeps only its on/off switch.
   */
  @Input() showButtons = true;

  /**
   * Emitted when user initiates execution or cancellation of the action.
   */
  @Output() onExecute = new EventEmitter<ChatV2ActionExecuteEvent>();

  /**
   * Alias for onExecute complying with standard non-prefixed output naming.
   */
  @Output() execute = this.onExecute;

  /**
   * Emitted when user toggles the active/inactive switch for this action card.
   */
  @Output() onToggleActive = new EventEmitter<ChatV2ActionToggleEvent>();

  /**
   * Alias for onToggleActive complying with standard non-prefixed output naming.
   */
  @Output() toggleActiveState = this.onToggleActive;

  /**
   * Whether the JSON parameter inspection drawer is currently expanded.
   */
  public isDetailsOpen = false;

  /**
   * Tracks whether the local user choice was confirmed (true) or cancelled/skipped (false).
   */
  public lastExecutionConfirmed: boolean | null = null;

  /**
   * Whether the action has concluded (from upstream status, local optimistic click,
   * or because a newer message arrived after this message in the thread).
   */
  public get isActionDone(): boolean {
    return !this.isLatest || this.isCompleted || this.isOptimisticDone;
  }

  /**
   * Whether the action was actually executed and confirmed (not skipped, declined, or left unexecuted on an older message).
   */
  public get isActionProcessed(): boolean {
    return (
      (this.isCompleted || this.isOptimisticDone) &&
      this.decisionConfirmed !== false
    );
  }

  /**
   * Whether the toggle switch is permanently locked (when the message is no longer the latest,
   * or the action has completed in the store, or it was processed).
   */
  public get isToggleDisabled(): boolean {
    return !this.isLatest || this.isCompleted || this.isActionProcessed;
  }

  /**
   * Whether the action is currently in a skipped/inactive state (toggled off before submission,
   * declined upon submission, or left unexecuted on an older message).
   */
  public get isSkippedState(): boolean {
    return !this.isActive || (this.isActionDone && !this.isActionProcessed);
  }

  /**
   * Whether the card's include switch should render in the ON state.
   */
  public get isSwitchOn(): boolean {
    return !this.isSkippedState;
  }

  /**
   * The decision shown once the action is done: the card's own click, else — for a card decided
   * by the batch bar — declined when the user switched it off.
   *
   * @returns True/false for a known decision, null when it was confirmed by default.
   */
  public get decisionConfirmed(): boolean | null {
    if (this.lastExecutionConfirmed !== null) {
      return this.lastExecutionConfirmed;
    }
    if (this.action?.executionState?.response?.confirmed === false) {
      return false;
    }
    if (this.action?.executionState?.response?.confirmed === true) {
      return true;
    }
    return this.isActive ? null : false;
  }

  /**
   * Whether the action represents an async workflow run.
   */
  public get isAsync(): boolean {
    return isAsyncAction(this.action);
  }

  /**
   * Whether the action is a backend MCP / server action tool.
   */
  public get isServer(): boolean {
    return isActionServerExecution(this.action);
  }

  /**
   * Extracted HTTP info (method, path) for endpoint actions.
   */
  public get httpInfo(): ChatV2HttpInfo | null {
    return getActionHttpInfo(this.action);
  }

  /**
   * Primary display title for the card header.
   */
  public get displayTitle(): string {
    return getActionDisplayTitle(this.action);
  }

  /**
   * Secondary display description for the card subtitle row.
   */
  public get displayDescription(): string {
    return getActionDisplayDescription(this.action);
  }

  /**
   * Cleansed JSON display payload for the details drawer.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get displayPayload(): any {
    return getActionDisplayPayload(this.action);
  }

  /**
   * Whether the action has a non-empty JSON payload to render inside the details drawer.
   */
  public get hasDisplayPayload(): boolean {
    return hasActionDisplayPayload(this.action);
  }

  /**
   * Contextual action button labels and styling variant.
   */
  public get buttonLabels(): ChatV2ActionButtonLabels {
    return getOperationButtonLabels(this.action);
  }

  /**
   * Pure helper for HTTP method badge classes exposed to template.
   */
  public getHttpMethodBadgeClass = getHttpMethodBadgeClass;

  /**
   * Toggles the active/inactive state of the action while the message is still the latest
   * and the action/batch has not yet been finalized in the store.
   *
   * @param event - Optional DOM event to stop bubbling.
   */
  public toggleActive(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isToggleDisabled) {
      return;
    }
    if (this.isSkippedState) {
      this.isOptimisticDone = false;
      this.lastExecutionConfirmed = null;
      this.isActive = true;
    } else {
      this.isActive = false;
    }
    this.onToggleActive.emit({
      action: this.action,
      isActive: this.isActive,
    });
    this.cdr.markForCheck();
  }

  /**
   * Toggles the open/closed state of the JSON parameter details drawer.
   *
   * @param event - Optional DOM event to stop bubbling.
   */
  public toggleDetails(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isDetailsOpen = !this.isDetailsOpen;
    this.cdr.markForCheck();
  }

  /**
   * Handles user click on contextual action buttons.
   * Triggers immediate optimistic transition in-place and notifies parent.
   *
   * @param confirmed - True if approved, false if cancelled/discarded.
   * @param options - Optional flags (e.g. { alwaysAllow: true }).
   * @param event - Optional DOM event to stop bubbling.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public handleExecute(confirmed: boolean, options?: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.isActionDone) {
      return;
    }

    this.isOptimisticDone = true;
    this.lastExecutionConfirmed = confirmed;

    this.onExecute.emit({
      action: this.action,
      confirmed,
      options,
    });

    this.cdr.markForCheck();
  }
}
